import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Typography,
  Card,
  CardContent,
  TextField,
  Tooltip,
  Button,
  Autocomplete,
  FormControlLabel,
  Box,
  Grid,
  Checkbox,
  IconButton,
  InputAdornment,
  styled,
  CircularProgress,
  Alert,
  Link,
  Snackbar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import axiosInstance from './axiosInstance';

// Styled Checkbox
const StyledCheckbox = styled((props) => <Checkbox {...props} />)({
  '&.Mui-checked': {
    color: '#3f51b5',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 28,
  },
});

// Date Validation Function
const validateDate = (dateString) => {
  const regexp = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  if (!regexp.test(dateString)) return false;

  const [day, month, year] = dateString.match(regexp).slice(1);
  const date = new Date(`${year}-${month}-${day}`);

  return (
    date.getDate() === parseInt(day, 10) &&
    date.getMonth() + 1 === parseInt(month, 10) &&
    date.getFullYear() === parseInt(year, 10)
  );
};

// Date Input Formatter
const formatInputDate = (value) => {
  const cleaned = value.replace(/[^0-9]/g, '');
  const match = cleaned.match(/(\d{1,2})(\d{0,2})(\d{0,4})/);
  if (match) {
    const part1 = match[1];
    const part2 = match[2] ? '/' + match[2] : '';
    const part3 = match[3] ? '/' + match[3] : '';
    return `${part1}${part2}${part3}`;
  }
  return value;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    if (cleaned === '') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

const toDisplayDate = (value) => {
  if (!value) return '';
  let working = value;
  if (typeof working !== 'string') {
    return '';
  }
  if (working.includes('T')) {
    working = working.split('T')[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(working)) {
    const [year, month, day] = working.split('-');
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(working)) {
    const [day, month, year] = working.split('/');
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  return working;
};

const toIsoDate = (value) => {
  if (!value) return '';
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) {
    return '';
  }
  const [ , day, month, year ] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const normalizeId = (value) => (typeof value === 'string' ? value.toLowerCase() : value);
const normalizeValueForValidation = (field, value) => {
  if (!DATE_FIELDS.has(field) || typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const displayFormatted = toDisplayDate(trimmed);
  return displayFormatted || trimmed;
};

const REQUIRED_FIELD_ERROR = 'This field is required';
const STRICT_DOC_TYPES = new Set(['invoice', 'estimate', 'credit_note']);
const APPROVABLE_DOC_TYPES = new Set(['invoice', 'estimate', 'credit_note', 'statement']);
const DEFAULT_VALID_DOC_TYPES = new Set(['invoice', 'credit_note']);
const AUTO_INCLUDE_IN_NET_TOTALS_DOC_TYPES = new Set(['invoice', 'credit_note']);
const EXCLUDED_AUTO_INCLUDE_STATUSES = new Set(['rejected', 'in_review']);
const STATEMENT_DOC_TYPE = 'statement';
const STATEMENT_REQUIRED_FIELDS = ['location_id', 'doc_status_id', 'invoice_date', 'posting_date', 'total'];
const OTHER_DOC_TYPE = 'other';
const ALLOWED_STATUSES_FOR_OTHER = new Set(['in_progress', 'in_review', 'rejected', 'invalid_document']);
const ALLOWED_STATUSES_FOR_STATEMENT = new Set(['approved', 'in_progress', 'in_review', 'rejected']);
const STRICT_DOC_TYPE_DISALLOWED_STATUSES = new Set(['invalid_document']);
const STRICT_REQUIRED_FIELDS = [
  'location_id',
  'supplier_id',
  'doc_status_id',
  'nominal_code_id',
  'invoice_date',
  'posting_date',
  'invoice_number',
  'order_number',
  'net',
  'vat',
  'total',
];

const HALF_WIDTH_FIELDS = new Set(['doc_type_id', 'doc_status_id', 'invoice_date', 'posting_date', 'invoice_number', 'order_number']);
const DATE_FIELDS = new Set(['invoice_date', 'posting_date']);
const ALWAYS_REQUIRED_FIELDS = new Set(['doc_type_id', 'doc_status_id', 'tax_code_id', 'invoice_date', 'posting_date']);
const VALIDATED_FIELDS = [
  'location_id',
  'supplier_id',
  'doc_type_id',
  'doc_status_id',
  'supplier_mapping_id',
  'supplier_name',
  'supplier_address',
  'nominal_code_id',
  'invoice_date',
  'posting_date',
  'invoice_number',
  'order_number',
  'net',
  'vat',
  'total',
  'tax_code_id',
  'credit_amount_requested',
  'credit_amount_received',
];
const VALIDATED_FIELD_SET = new Set(VALIDATED_FIELDS);
const PRIMARY_FIELDS = ['location_id', 'supplier_id', 'nominal_code_id', 'doc_type_id', 'doc_status_id', 'invoice_date', 'posting_date', 'invoice_number', 'order_number'];

// Extracted Fields Component
const ExtractedFields = ({
  tempEditableData,
  sites,
  suppliers,
  nominalCodes,
  docTypes,
  docStatuses,
  taxCodes = [],
  handleFieldChange,
  validationErrors,
  setValidationErrors,
  dateInput,
  setDateInput,
  isEdited,
  handleSaveChanges,
  onApproveDocument,
  onRejectDocument,
  docRejectReasons = [],
  getLocationOptionLabel,
  getSupplierOptionLabel,
  getNominalCodeOptionLabel,
  getDocTypeOptionLabel,
  getDocStatusOptionLabel,
  refetchData,
  imageName,
  setPolygonProperty,
  validateAllFields,
  snackbarAnchor = { vertical: 'bottom', horizontal: 'center' },
  snackbarSx = undefined,
  isReadOnly = false,
  originalData = null
}) => {
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [supplierMapping, setSupplierMapping] = useState(null);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [topFilteredOption, setTopFilteredOption] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [rejectMenuAnchor, setRejectMenuAnchor] = useState(null);
  const [nominalCodeOptionsRaw, setNominalCodeOptionsRaw] = useState(() => (Array.isArray(nominalCodes) ? nominalCodes : []));
  const [localFieldValues, setLocalFieldValues] = useState({});
  const invoiceDatePickerRef = useRef(null);
  const postingDatePickerRef = useRef(null);
  const validationTimeoutRef = useRef(null);
  const validDocumentTouchedRef = useRef(false);

  const scheduleValidation = useCallback(() => {
    if (typeof validateAllFields !== 'function') return;
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    validationTimeoutRef.current = setTimeout(() => {
      validationTimeoutRef.current = null;
      validateAllFields();
    }, 0);
  }, [validateAllFields]);

  const changeField = useCallback((field, value) => {
    setLocalFieldValues((prev) => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
    handleFieldChange(field, value);
    scheduleValidation();
  }, [handleFieldChange, scheduleValidation]);

  const applyEmailUploaderFlag = useCallback((value) => {
    changeField('email_uploader', Boolean(value));
  }, [changeField]);

  const clearRejectedFinancialFlags = useCallback(() => {
    validDocumentTouchedRef.current = true;
    changeField('include_in_net_totals', false);
    changeField('credit_requested', false);
  }, [changeField]);

  useEffect(() => () => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(nominalCodes) && nominalCodes.length > 0) {
      setNominalCodeOptionsRaw(nominalCodes);
    }
  }, [nominalCodes]);

  useEffect(() => {
    validDocumentTouchedRef.current = false;
  }, [tempEditableData?.id]);

  useEffect(() => {
    let cancelled = false;
    const needsFetch = !Array.isArray(nominalCodes) || nominalCodes.length === 0;
    if (!needsFetch) return () => {
      cancelled = true;
    };

    const fetchNominalCodes = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/nominal-code');
        if (cancelled) return;
        const payload = Array.isArray(response?.data?.data) ? response.data.data : [];
        setNominalCodeOptionsRaw(payload);
      } catch (error) {
        console.error('Error fetching nominal codes:', error);
      }
    };

    fetchNominalCodes();

    return () => {
      cancelled = true;
    };
  }, [nominalCodes]);

  const getEffectiveFieldValue = useCallback((field) => (
    Object.prototype.hasOwnProperty.call(localFieldValues, field)
      ? localFieldValues[field]
      : tempEditableData?.[field]
  ), [localFieldValues, tempEditableData]);

  useEffect(() => {
    setLocalFieldValues((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((field) => {
        const latest = tempEditableData?.[field];
        const pending = next[field];
        const bothFalsy = (pending == null || pending === '') && (latest == null || latest === '');
        const stringsEqual = typeof pending === 'string' && typeof latest === 'string' && pending.trim() === latest.trim();
        const scalarEqual = typeof pending !== 'object' && typeof latest !== 'object' && String(pending ?? '') === String(latest ?? '');
        if (pending === latest || bothFalsy || stringsEqual || scalarEqual) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [tempEditableData]);

  const uniqueNominalCodes = useMemo(() => {
    if (!Array.isArray(nominalCodeOptionsRaw)) return [];
    const seen = new Map();
    nominalCodeOptionsRaw.forEach((entry) => {
      if (!entry) return;
      const rawId = entry.nominal_code_id;
      const key = typeof rawId === 'string'
        ? rawId.trim().toLowerCase()
        : String(rawId ?? '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.set(key, entry);
    });
    return Array.from(seen.values());
  }, [nominalCodeOptionsRaw]);

  const docTypeIdRaw = getEffectiveFieldValue('doc_type_id');
  const docTypeId = normalizeId(docTypeIdRaw);
  const docStatusId = getEffectiveFieldValue('doc_status_id');
  const isRejectedStatus = docStatusId === 'rejected';
  const isAutoIncludeInNetTotalsDocType = docTypeId && AUTO_INCLUDE_IN_NET_TOTALS_DOC_TYPES.has(docTypeId);
  const isIncludeInNetTotalsLocked = Boolean(isAutoIncludeInNetTotalsDocType && docStatusId);
  const forcedIncludeInNetTotalsValue = Boolean(
    isAutoIncludeInNetTotalsDocType
      && docStatusId
      && !EXCLUDED_AUTO_INCLUDE_STATUSES.has(docStatusId)
  );
  const includeInNetTotalsChecked = isIncludeInNetTotalsLocked
    ? forcedIncludeInNetTotalsValue
    : Boolean(getEffectiveFieldValue('include_in_net_totals'));
  const isStrictDocType = docTypeId && STRICT_DOC_TYPES.has(docTypeId);
  const canBeApproved = docTypeId && APPROVABLE_DOC_TYPES.has(docTypeId);
  const isStatement = docTypeId === STATEMENT_DOC_TYPE;
  const isOtherDocType = docTypeId === OTHER_DOC_TYPE;
  const selectedSupplierOption = useMemo(() => {
    const supplierId = getEffectiveFieldValue('supplier_id');
    if (!supplierId || !Array.isArray(suppliers)) return null;
    return suppliers.find((supplier) => supplier?.supplier_id === supplierId) || null;
  }, [getEffectiveFieldValue, suppliers]);

  const receivedDiscountPercent = useMemo(() => {
    const directDiscount = toNumberOrNull(getEffectiveFieldValue('discount'));
    if (directDiscount !== null) {
      return Math.max(directDiscount, 0);
    }

    const actualPrice = toNumberOrNull(getEffectiveFieldValue('actual_price'));
    const retailPrice = toNumberOrNull(getEffectiveFieldValue('retail_price'));
    if (actualPrice === null || retailPrice === null || actualPrice <= 0 || retailPrice <= 0) {
      return null;
    }
    const discount = (1 - actualPrice / retailPrice) * 100;
    return Number.isFinite(discount) ? Math.max(roundCurrency(discount), 0) : null;
  }, [getEffectiveFieldValue]);

  const requestedCreditAmount = useMemo(() => {
    const agreedDiscount = Math.max(toNumberOrNull(selectedSupplierOption?.discount) ?? 0, 0);
    const receivedDiscount = Math.max(receivedDiscountPercent ?? 0, 0);
    const missingDiscount = Math.max(agreedDiscount - receivedDiscount, 0);
    const retailAmount = Math.abs(toNumberOrNull(getEffectiveFieldValue('retail_price')) ?? 0);
    const netAmount = Math.abs(toNumberOrNull(getEffectiveFieldValue('net')) ?? 0);
    const baseAmount = retailAmount > 0
      ? retailAmount
      : (netAmount > 0 ? netAmount : Math.abs(toNumberOrNull(getEffectiveFieldValue('total')) ?? 0));

    return roundCurrency((baseAmount * missingDiscount) / 100);
  }, [getEffectiveFieldValue, receivedDiscountPercent, selectedSupplierOption]);

  useEffect(() => {
    if (isIncludeInNetTotalsLocked && tempEditableData?.include_in_net_totals !== forcedIncludeInNetTotalsValue) {
      validDocumentTouchedRef.current = false;
      changeField('include_in_net_totals', forcedIncludeInNetTotalsValue);
    }
    if (tempEditableData?.doc_status_id === 'rejected' && tempEditableData?.credit_requested) {
      changeField('credit_requested', false);
    }
  }, [
    isIncludeInNetTotalsLocked,
    forcedIncludeInNetTotalsValue,
    tempEditableData?.doc_status_id,
    tempEditableData?.include_in_net_totals,
    tempEditableData?.credit_requested,
    changeField,
  ]);

  const rejectableReasons = useMemo(() => {
    const baseReasons = Array.isArray(docRejectReasons)
      ? docRejectReasons.filter((reason) => reason?.allow_manual_selection)
      : [];
    const syntheticEmailInvalidReason = {
      doc_reject_reason_id: 'invalid_document',
      name: 'Invalid Document – Email Uploader',
      allow_manual_selection: true,
      syntheticEmailUploader: true,
      syntheticKey: 'invalid_document_email',
    };
    return [...baseReasons, syntheticEmailInvalidReason];
  }, [docRejectReasons]);

  const handleApproveClick = async () => {
    if (typeof onApproveDocument !== 'function') return;
    if (isReadOnly) return;
    if (!canBeApproved) return;
    if (!allFieldsFilled()) return;
    if (loading || actionLoading) return;
    setActionLoading(true);
    try {
      await onApproveDocument();
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = async (event) => {
    if (typeof onRejectDocument !== 'function') return;
    if (isReadOnly) return;
    if (!canBeApproved && !isOtherDocType) return;
    if (isOtherDocType) {
      if (loading || actionLoading) return;
      applyEmailUploaderFlag(false);
      clearRejectedFinancialFlags();
      setActionLoading(true);
      try {
        await onRejectDocument({
          statusId: 'rejected',
          reasonId: 'invalid_document',
          emailUploader: false,
        });
      } finally {
        setActionLoading(false);
      }
      return;
    }
    setRejectMenuAnchor(event.currentTarget);
  };

  const handleRejectOptionSelect = async (reason) => {
    setRejectMenuAnchor(null);
    if (typeof onRejectDocument !== 'function') return;
    if (!reason) return;
    if (loading || actionLoading) return;
    const reasonId = reason.doc_reject_reason_id;
    const syntheticEmail = Boolean(reason.syntheticEmailUploader);
    applyEmailUploaderFlag(syntheticEmail);
    clearRejectedFinancialFlags();
    changeField('doc_status_id', 'rejected');
    changeField('doc_reject_reason_id', reasonId);
    const payload = {
      statusId: 'rejected',
      reasonId,
      emailUploader: syntheticEmail,
    };
    setActionLoading(true);
    try {
      await onRejectDocument(payload);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (!canBeApproved || isOtherDocType || actionLoading) {
      setRejectMenuAnchor(null);
    }
  }, [canBeApproved, isOtherDocType, actionLoading]);


  const computeStatusOptions = useCallback((typeId) => {
    if (!Array.isArray(docStatuses)) return [];
    const normalizedType = normalizeId(typeId);
    if (!normalizedType) return docStatuses;

    if (normalizedType === OTHER_DOC_TYPE) {
      return docStatuses.filter((status) => ALLOWED_STATUSES_FOR_OTHER.has(status.doc_status_id));
    }

    if (normalizedType === STATEMENT_DOC_TYPE) {
      return docStatuses.filter((status) => ALLOWED_STATUSES_FOR_STATEMENT.has(status.doc_status_id));
    }

    if (STRICT_DOC_TYPES.has(normalizedType)) {
      return docStatuses.filter((status) => !STRICT_DOC_TYPE_DISALLOWED_STATUSES.has(status.doc_status_id));
    }

    return docStatuses;
  }, [docStatuses]);

  const statusOptions = useMemo(() => computeStatusOptions(docTypeIdRaw), [computeStatusOptions, docTypeIdRaw]);
  const trackedFieldsForChanges = ['location_id', 'supplier_id', 'doc_type_id', 'doc_status_id', 'nominal_code_id', 'invoice_date', 'posting_date', 'invoice_number', 'order_number', 'net', 'vat', 'total', 'tax_code_id', 'doc_reject_reason_id', 'include_in_net_totals', 'credit_requested', 'credit_amount_requested', 'credit_amount_received'];
  const numericChangeFields = new Set(['net', 'vat', 'total', 'credit_amount_requested', 'credit_amount_received']);
  const taxCodeOptions = Array.isArray(taxCodes) ? taxCodes : [];
  const invoiceDateDisplay = toDisplayDate(dateInput || tempEditableData?.invoice_date || '');
  const postingDateDisplay = toDisplayDate(tempEditableData?.posting_date || '');
  const invoiceIsoValue = toIsoDate(invoiceDateDisplay);
  const postingIsoValue = toIsoDate(postingDateDisplay);

  const formatMetaValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const stringValue = String(value).trim();
    return stringValue || 'N/A';
  };

  const uploadedOnDisplay = toDisplayDate(tempEditableData?.uploaded_on) || 'N/A';
  const uploadedByDisplay = useMemo(() => {
    const rawValue = formatMetaValue(tempEditableData?.uploaded_by);
    if (rawValue === 'N/A') return '-';

    const lowerCased = rawValue.toLowerCase();
    if (lowerCased.endsWith('@haloarc.co.uk')) {
      const atIndex = rawValue.indexOf('@');
      if (atIndex > -1) {
        return rawValue.slice(0, atIndex);
      }
    }
    return rawValue;
  }, [tempEditableData?.uploaded_by]);
  const repairSiteDisplay = useMemo(() => {
    if (!tempEditableData?.repair_location_id) return '-';

    const repairLocation = Array.isArray(sites)
      ? sites.find((location) => location.location_id === tempEditableData.repair_location_id)
      : null;

    if (repairLocation) {
      if (typeof getLocationOptionLabel === 'function') {
        return getLocationOptionLabel(repairLocation);
      }
      const parts = [repairLocation.location_id, repairLocation.name].filter(Boolean);
      const label = parts.join(' - ').trim();
      return label || repairLocation.location_id || '-';
    }

    const fallback = formatMetaValue(tempEditableData.repair_location_id);
    return fallback === 'N/A' ? '-' : fallback;
  }, [sites, tempEditableData?.repair_location_id, getLocationOptionLabel]);

  const topMetaFields = useMemo(
    () => [
      { label: 'Uploaded On', value: uploadedOnDisplay, align: 'left' },
      { label: 'Uploaded By', value: uploadedByDisplay, align: 'center' },
      { label: 'Repair Site', value: repairSiteDisplay, align: 'right' },
    ],
    [uploadedOnDisplay, uploadedByDisplay, repairSiteDisplay]
  );
  const normalizeFieldValue = (field, value) => {
    if (value === null || value === undefined) return '';
    if (numericChangeFields.has(field)) {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return '';
      return numeric.toFixed(2);
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value).trim();
  };

  const currentRejectReasonLabel = useMemo(() => {
    if (!tempEditableData?.doc_reject_reason_id) return '';
    const match = Array.isArray(docRejectReasons)
      ? docRejectReasons.find((reason) => reason.doc_reject_reason_id === tempEditableData.doc_reject_reason_id)
      : null;
    let label = match?.name || tempEditableData.doc_reject_reason_id;
    if (
      tempEditableData.doc_reject_reason_id === 'invalid_document' &&
      tempEditableData.email_uploader
    ) {
      label = `${label} (Emailed)`;
    }
    return label;
  }, [docRejectReasons, tempEditableData?.doc_reject_reason_id, tempEditableData?.email_uploader]);

  const hasUnsavedChanges = useMemo(() => {
    if (!originalData || !tempEditableData) return false;
    return trackedFieldsForChanges.some((field) => {
      const current = normalizeFieldValue(field, tempEditableData?.[field]);
      const original = normalizeFieldValue(field, originalData?.[field]);
      return current !== original;
    });
  }, [originalData, tempEditableData]);


  const isRequiredField = useCallback((field) => {
    if (ALWAYS_REQUIRED_FIELDS.has(field)) return true;
    if (isOtherDocType && field === 'location_id') return true;
    if (isStrictDocType) return STRICT_REQUIRED_FIELDS.includes(field);
    if (isStatement) return STATEMENT_REQUIRED_FIELDS.includes(field);
    return false;
  }, [isOtherDocType, isStrictDocType, isStatement]);

  const getFieldError = useCallback((field, value) => {
    if (!VALIDATED_FIELD_SET.has(field)) {
      return undefined;
    }

    const valueForValidation = normalizeValueForValidation(field, value);
    const isRequired = isRequiredField(field);
    const hasValue = !(valueForValidation === null || valueForValidation === undefined || valueForValidation === '' || (typeof valueForValidation === 'string' && valueForValidation.trim() === ''));

    if (!isRequired && !hasValue) {
      return undefined;
    }

    if (isRequired && !hasValue) {
      return REQUIRED_FIELD_ERROR;
    }

    if (DATE_FIELDS.has(field) && hasValue && !validateDate(valueForValidation)) {
      return 'Use dd/mm/yyyy date format.';
    }

    if (field === 'credit_amount_requested' || field === 'credit_amount_received') {
      const numericValue = Number(valueForValidation);
      if (Number.isNaN(numericValue)) {
        return 'Enter a valid amount.';
      }
      if (numericValue < 0) {
        return 'Amount cannot be negative.';
      }
    }

    return undefined;
  }, [isRequiredField]);

  const setFieldError = useCallback((field, error) => {
    setValidationErrors((prev = {}) => {
      if (prev[field] === error) return prev;
      const next = { ...prev };
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });
  }, []);

  const validateField = useCallback((field, value) => {
    if (!VALIDATED_FIELD_SET.has(field)) return;
    const error = getFieldError(field, value);
    setFieldError(field, error);
  }, [getFieldError, setFieldError]);

  const fieldErrors = useMemo(() => {
    const base = { ...(validationErrors || {}) };
    VALIDATED_FIELDS.forEach((field) => {
      const existing = base[field];
      if (existing && String(existing).trim().length > 0) {
        return;
      }

      const effectiveValue = getEffectiveFieldValue(field);
      const computed = getFieldError(field, effectiveValue);
      if (computed) {
        base[field] = computed;
      } else if (existing === undefined || existing === null || existing === '') {
        delete base[field];
      }
    });

    return base;
  }, [validationErrors, getFieldError, getEffectiveFieldValue]);

  const docStatusValue = getEffectiveFieldValue('doc_status_id');

  // Handle Field Blur
  const onFieldBlur = (field, value) => {
    if (isReadOnly) return;
    if (field === 'net' || field === 'vat') {
      const formattedValue = value === '' ? '0.00' : parseFloat(value).toFixed(2);
      changeField(field, formattedValue);
      validateField(field, formattedValue);
      return;
    }
    validateField(field, value);
    scheduleValidation();
  };

  const openDatePicker = (field) => {
    if (isReadOnly) return;
    const ref = field === 'invoice_date' ? invoiceDatePickerRef : postingDatePickerRef;
    if (ref.current) {
      if (typeof ref.current.showPicker === 'function') {
        ref.current.showPicker();
      } else {
        ref.current.click();
      }
    }
  };

  const handleDatePickerChange = (field) => (event) => {
    const isoValue = event.target.value;
    const formatted = toDisplayDate(isoValue);
    if (field === 'invoice_date') {
      setDateInput(formatted);
    }
    changeField(field, formatted);
    validateField(field, formatted);
    setIsSaved(false);
  };

  // Handle Date Change via manual typing
  const handleDateChange = (field) => (e) => {
    if (isReadOnly) return;
    const formattedDate = formatInputDate(e.target.value);
    if (field === 'invoice_date') {
      setDateInput(formattedDate);
    }
    changeField(field, formattedDate);
    setIsSaved(false);
  };

  // Capitalize Field
  const capitalizeField = (field) => {
    if (field === 'vat') {
      return 'VAT';
    }
    if (field === 'doc_type_id') return 'Document Type';
    if (field === 'doc_status_id') return 'Doc Status';
    if (field === 'invoice_date') return 'Document Date';
    if (field === 'invoice_number') return 'Document Number';
    if (field === 'tax_code_id') return 'Tax Code';
    if (field === 'include_in_net_totals') return 'Include in Net Totals';
    if (field === 'credit_requested') return 'Request Credit';
    if (field === 'credit_amount_requested') return 'Credit Requested';
    if (field === 'credit_amount_received') return 'Credit Received';
    return field
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getFieldLabel = (field) => {
    const baseLabel = capitalizeField(field);
    return isRequiredField(field) ? `${baseLabel} *` : baseLabel;
  };


  const handleSupplierMappingFieldChange = (field, value) => {
    setSupplierMapping((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const closeSupplierMappingCard = () => {
    setSupplierMapping(null);
    ['supplier_mapping_id', 'supplier_name', 'supplier_address', 'supplier_telephone', 'supplier_email', 'supplier_website'].forEach((field) => setFieldError(field, undefined));
  };

  const focusPolygon = (property) => {
    if (!property) return;
    setPolygonProperty(`${property}::${Date.now()}`);
  };

  const handleSupplierMapping = async () => {
    if (isReadOnly) return;
    const {
      supplier_id,
      supplier_name,
      supplier_address,
      supplier_telephone,
      supplier_email,
      supplier_website
    } = tempEditableData;
    setSupplierMapping({
      supplier_mapping_id: supplier_id,
      supplier_name,
      supplier_address,
      supplier_telephone,
      supplier_email,
      supplier_website
    });
  };

  useEffect(() => {
    if (supplierMapping) {
      ['supplier_mapping_id', 'supplier_name', 'supplier_address', 'supplier_telephone', 'supplier_email', 'supplier_website'].forEach((field) => {
        validateField(field, supplierMapping[field])
      });
    }
  }, [supplierMapping])

  const createSupplierMapping = async () => {
    // Ensure only the necessary fields are sent
    
    const formattedData = {
      supplier_id: supplierMapping.supplier_mapping_id,
      name: supplierMapping.supplier_name,
      address: supplierMapping.supplier_address,
      telephone: supplierMapping.supplier_telephone,
      email: supplierMapping.supplier_email,
      website: supplierMapping.supplier_website
    };
    
    try {
      const response = await axiosInstance.post(`/api/v1/supplier/mapping`, formattedData, {
        headers: {
          'Content-Type': 'application/json'
        },
      });
      
      //console.log('Update successful:', response.data);
      setStatusMessage({ type: 'success', message: 'Supplier mapping created successfully.' });
      setSupplierMapping(null);
      return response.data; // Return response data to check success in calling functions
    } catch (error) {
      console.error('Error creating supplier mapping:', error.response.data);
      setStatusMessage({ type: 'error', message: `Error while creating supplier mapping. ${JSON.stringify(error?.response?.data?.error? error?.response?.data?.error: error?.response?.data?.errors)}` });
    }
  };


  const handleFocusEvent = (property) => {
    focusPolygon(property);
    setTopFilteredOption(null);
    setFilterValue(null);
  };


  // Filter Options
  const filterOptions = (options, state, isSupplier) => {
    const inputValue = state.inputValue.toLowerCase();
    setFilterValue(inputValue);
    if (isSupplier) {
      options = options.filter(
        (option) =>
          option.supplier_id?.toLowerCase().includes(inputValue) ||
          option.name?.toLowerCase().includes(inputValue)
      );
      if (options?.length > 0) {
        setTopFilteredOption(options[0]);
      }
      return options;
    }

    const startsWithOptions = options.filter(
      (option) =>
        option.location_id?.toString().toLowerCase().startsWith(inputValue) ||
        option.name?.toLowerCase().startsWith(inputValue)
    );

    if (startsWithOptions.length > 0) {
      setTopFilteredOption(startsWithOptions[0]);
      return startsWithOptions;
    }

    options = options.filter(
      (option) =>
        option.location_id?.toString().toLowerCase().includes(inputValue) ||
        option.name?.toLowerCase().includes(inputValue)
    );
    
    if (options?.length > 0) {
      setTopFilteredOption(options[0]);
    }

    return options
  };

  const filterDocTypeOptions = (options, state) => {
    const inputValue = state.inputValue.toLowerCase();
    setFilterValue(inputValue);

    const startsWithOptions = options.filter(
      (option) =>
        option.doc_type_id?.toLowerCase().startsWith(inputValue) ||
        option.name?.toLowerCase().startsWith(inputValue)
    );

    if (startsWithOptions.length > 0) {
      setTopFilteredOption(startsWithOptions[0]);
      return startsWithOptions;
    }

    const containsOptions = options.filter(
      (option) =>
        option.doc_type_id?.toLowerCase().includes(inputValue) ||
        option.name?.toLowerCase().includes(inputValue)
    );

    if (containsOptions.length > 0) {
      setTopFilteredOption(containsOptions[0]);
    }

    return containsOptions;
  };

  const filterDocStatusOptions = (options, state) => {
    const inputValue = state.inputValue.toLowerCase();
    setFilterValue(inputValue);

    const startsWithOptions = options.filter(
      (option) =>
        option.doc_status_id?.toLowerCase().startsWith(inputValue) ||
        option.name?.toLowerCase().startsWith(inputValue)
    );

    if (startsWithOptions.length > 0) {
      setTopFilteredOption(startsWithOptions[0]);
      return startsWithOptions;
    }

    const containsOptions = options.filter(
      (option) =>
        option.doc_status_id?.toLowerCase().includes(inputValue) ||
        option.name?.toLowerCase().includes(inputValue)
    );

    if (containsOptions.length > 0) {
      setTopFilteredOption(containsOptions[0]);
    }

    return containsOptions;
  };

  // Filter Nominal Code Options
const filterNominalCodeOptions = (options, state) => {
  const inputValue = state.inputValue.toLowerCase();
  setFilterValue(inputValue);
  const startsWithOptions = options.filter(
    (option) =>
        option.nominal_code_id?.toLowerCase().startsWith(inputValue) ||
        option.name?.toLowerCase().startsWith(inputValue)
    );

    if (startsWithOptions.length > 0) {
      setTopFilteredOption(startsWithOptions[0]);
      return startsWithOptions;
    }



    options = options.filter(
      (option) =>
        option.nominal_code_id?.toLowerCase().includes(inputValue) ||
        option.name?.toLowerCase().includes(inputValue)
    );

    if (options?.length > 0) {
      setTopFilteredOption(options[0]);
    }

  return options;
};

const filterTaxCodeOptions = (options, state) => {
  const inputValue = state.inputValue.toLowerCase();
  setFilterValue(inputValue);

  const startsWithOptions = options.filter(
    (option) =>
      option.tax_code_id?.toLowerCase().startsWith(inputValue) ||
      option.name?.toLowerCase().startsWith(inputValue)
  );

  if (startsWithOptions.length > 0) {
    setTopFilteredOption(startsWithOptions[0]);
    return startsWithOptions;
  }

  const containsOptions = options.filter(
    (option) =>
      option.tax_code_id?.toLowerCase().includes(inputValue) ||
      option.name?.toLowerCase().includes(inputValue)
  );

  if (containsOptions.length > 0) {
    setTopFilteredOption(containsOptions[0]);
  }

  return containsOptions;
};

  // Check if all fields are filled
  const allFieldsFilled = () => {
    const requiredFields = ['doc_type_id', 'doc_status_id', 'tax_code_id', 'invoice_date', 'posting_date'];
    if (isStrictDocType) requiredFields.push(...STRICT_REQUIRED_FIELDS);
    else if (isStatement) requiredFields.push(...STATEMENT_REQUIRED_FIELDS);
    else if (isOtherDocType) requiredFields.push('location_id');

    return requiredFields.every((field) => {
      const value = getEffectiveFieldValue(field);
      return !(value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === ''));
    });
  };

  useEffect(() => {
    setValidationErrors((prev = {}) => {
      let changed = false;
      const next = { ...prev };
      VALIDATED_FIELDS.forEach((field) => {
        const value = getEffectiveFieldValue(field);
        const error = getFieldError(field, value);
        if (next[field] !== error) {
          next[field] = error;
          changed = true;
        }
      });
      // Remove stale keys that are no longer tracked
      Object.keys(next).forEach((field) => {
        if (!VALIDATED_FIELD_SET.has(field)) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [tempEditableData, getFieldError, getEffectiveFieldValue]);

  useEffect(() => {
    const hasDocStatusValue = !(
      docStatusValue === null ||
      docStatusValue === undefined ||
      docStatusValue === '' ||
      (typeof docStatusValue === 'string' && docStatusValue.trim() === '')
    );

    if (!hasDocStatusValue && fieldErrors?.doc_status_id !== REQUIRED_FIELD_ERROR) {
      setFieldError('doc_status_id', REQUIRED_FIELD_ERROR);
    }
  }, [docTypeId, docStatusValue, fieldErrors?.doc_status_id]);

  useEffect(() => {
    if (!canBeApproved && docStatusValue === 'approved') {
      changeField('doc_status_id', '');
      setValidationErrors((prev) => {
        const next = { ...prev };
        next.doc_status_id = REQUIRED_FIELD_ERROR;
        return next;
      });
      setIsSaved(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBeApproved, docStatusValue]);

  useEffect(() => {
    const currentStatus = docStatusValue;

    if (!Array.isArray(statusOptions)) {
      if (currentStatus) {
        changeField('doc_status_id', '');
      }
      setFieldError('doc_status_id', REQUIRED_FIELD_ERROR);
      setIsSaved(false);
      return;
    }

    const allowedStatusIds = new Set(statusOptions.map((status) => status.doc_status_id));
    const statusIsAllowed = allowedStatusIds.has(currentStatus);

    if (!statusIsAllowed) {
      if (currentStatus) {
        changeField('doc_status_id', '');
      }
      setFieldError('doc_status_id', REQUIRED_FIELD_ERROR);
      setIsSaved(false);
    } else if (fieldErrors?.doc_status_id === REQUIRED_FIELD_ERROR) {
      setFieldError('doc_status_id', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions, docStatusValue, fieldErrors?.doc_status_id]);

  const hasValidationErrors = useMemo(() => Object.values(fieldErrors || {}).some((value) => Boolean(value)), [fieldErrors]);

  const allRequiredFieldsSatisfied = allFieldsFilled() && !hasValidationErrors;
  const approveDisabled = loading || actionLoading || isReadOnly || !canBeApproved || !allRequiredFieldsSatisfied;
  const rejectDisabled = loading || actionLoading || isReadOnly || (!canBeApproved && !isOtherDocType);
  const isRejectMenuOpen = Boolean(rejectMenuAnchor);

  const handleSave = async () => {
    setLoading(true);
    try {
      await handleSaveChanges();
      setIsSaved(true);
    } catch (error) {
      setStatusMessage({ type: 'error', message: 'Failed to save changes. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' && filterValue) {
      if (supplierMapping) {
        if (topFilteredOption?.supplier_id) {
          supplierMapping.supplier_mapping_id = topFilteredOption?.supplier_id;
  
        }
      } else {
      if (topFilteredOption?.supplier_id) {
        tempEditableData.supplier_id = topFilteredOption.supplier_id;

      } else if (topFilteredOption?.doc_type_id) {
        tempEditableData.doc_type_id = topFilteredOption.doc_type_id;

      } else if (topFilteredOption?.doc_status_id) {
        tempEditableData.doc_status_id = topFilteredOption.doc_status_id;

      } else if (topFilteredOption?.location_id) {
        tempEditableData.location_id = topFilteredOption.location_id;

      } else if (topFilteredOption?.nominal_code_id) {
        tempEditableData.nominal_code_id = topFilteredOption.nominal_code_id;

      } else if (topFilteredOption?.tax_code_id) {
        tempEditableData.tax_code_id = topFilteredOption.tax_code_id;

        }
      }
      //event.preventDefault(); // Prevent default Tab behavior
    }
  };

  // Render
  return ( 
    <> 
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}> 
        {
          !supplierMapping && <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}> 
            {/* <Tooltip title={"Job Number Not Editable"}>
              <span>
                <Typography variant="h6" sx={{ fontWeight: "bold" }} display="inline"> 
                  Job Number: 
                </Typography> 
                <Typography variant="h6" display="inline" gutterBottom>
                  &nbsp;
                </Typography>
                <Typography variant="h6" display="inline" gutterBottom> 
                  {tempEditableData.job_id?.toUpperCase()}
                </Typography>
              </span>
            </Tooltip> */}
            
            
            {/* <Typography variant="h6" display="block" gutterBottom sx={{ fontStyle: 'italic', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'}}> 
              {imageName}
            </Typography> */}
            
            <Box position="relative" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.65)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 4,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={32} thickness={4} />
                </Box>
              )}
              <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1.5,
                  mb: 0,
                }}
              >
                {topMetaFields.map((field) => (
                  <Box
                    key={field.label}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      alignItems:
                        field.align === 'center'
                          ? 'center'
                          : field.align === 'right'
                            ? 'flex-end'
                            : 'flex-start',
                      textAlign:
                        field.align === 'center'
                          ? 'center'
                          : field.align === 'right'
                            ? 'right'
                            : 'left',
                    }}
                  >
                    <Typography align={field.align} sx={{ fontWeight: 'bold' }}>
                      {field.label}
                    </Typography>
                    <Typography align={field.align} sx={{ color: 'text.secondary' }}>
                      {field.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Grid container spacing={1.5} sx={{ mt: -0.25, flexGrow: 1 }}>
                {PRIMARY_FIELDS.map((field) => {
                  const isHalfWidth = HALF_WIDTH_FIELDS.has(field);
                  const gridProps = isHalfWidth ? { xs: 12, sm: 6, md: 6 } : { xs: 12 };
                  return (
                    <Grid item key={field} {...gridProps}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography align="left" sx={{ fontWeight: 'bold' }}>
                          {getFieldLabel(field)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {field === 'doc_status_id' && tempEditableData?.doc_reject_reason_id ? (
                            <Typography align="right" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {currentRejectReasonLabel}
                            </Typography>
                          ) : null}
                          {field === 'supplier_id' && !isReadOnly ? (
                            <Typography align="right" sx={{ cursor: 'pointer' }}>
                              <Link onClick={handleSupplierMapping}>Add new mapping?</Link>
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>

                      {field === 'location_id' ? (
                        <Autocomplete
                          disabled={isReadOnly}
                          options={sites}
                          value={tempEditableData.location_id ? sites.find((location) => location.location_id === tempEditableData.location_id) : null}
                          getOptionLabel={getLocationOptionLabel}
                          renderOption={(props, option) => (
                            <li {...props} key={option.location_id}>
                              <span>{option.location_id}</span>
                              <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                            </li>
                          )}
                          filterOptions={(options, state) => filterOptions(options, state, false)}
                          onChange={(event, newValue) => {
                            if (isReadOnly) return;
                            changeField('location_id', newValue ? newValue.location_id : '');
                            validateField('location_id', newValue ? newValue.location_id : '');
                            setIsSaved(false); // Reset isSaved on change
                          }}
                          onFocus={() => handleFocusEvent('customer_delivery_postcode')}
                          onBlur={(e) => onFieldBlur('location_id', e.target.value)}
                          isOptionEqualToValue={(option, value) => option.location_id === value?.location_id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required={isRequiredField('location_id')}
                              onKeyDown={handleKeyDown}
                              error={!!fieldErrors.location_id}
                              helperText={fieldErrors.location_id}
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      ) : field === 'supplier_id' ? (
                        <Autocomplete
                          disabled={isReadOnly}
                          options={suppliers}
                          value={tempEditableData.supplier_id ? suppliers.find((supplier) => supplier.supplier_id === tempEditableData.supplier_id) : null}
                          getOptionLabel={getSupplierOptionLabel}
                          renderOption={(props, option) => (
                            <li {...props} key={option.supplier_id}>
                              <span>{option.supplier_id}</span>
                              <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                            </li>
                          )}
                          filterOptions={(options, state) => filterOptions(options, state, true)}
                          onChange={(event, newValue) => {
                            if (isReadOnly) return;

                            const nextSupplierId = newValue ? newValue.supplier_id : '';
                            changeField('supplier_id', nextSupplierId);
                            validateField('supplier_id', nextSupplierId);

                            if (newValue) {
                              const nextNominalCode = newValue.nominal_code_id || null;
                              const nextTaxCode = newValue.tax_code_id || null;

                              if (nextNominalCode) {
                                changeField('nominal_code_id', nextNominalCode);
                                validateField('nominal_code_id', nextNominalCode);
                              }

                              if (nextTaxCode) {
                                changeField('tax_code_id', nextTaxCode);
                                validateField('tax_code_id', nextTaxCode);
                              }
                            }

                            setIsSaved(false); // Reset isSaved on change
                          }}
                          onBlur={(e) => onFieldBlur('supplier_id', e.target.value)}
                          onFocus={() => handleFocusEvent('supplier_name')}
                          isOptionEqualToValue={(option, value) => option.supplier_id === value?.supplier_id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required={isRequiredField('supplier_id')}
                              onKeyDown={handleKeyDown}
                              error={!!fieldErrors.supplier_id}
                              helperText={fieldErrors.supplier_id}
                              disabled={isReadOnly}
                            />
                          )}
                        />

                      ) : field === 'doc_type_id' ? (
                        <Autocomplete
                          disabled={isReadOnly}
                          options={docTypes || []}
                          value={tempEditableData.doc_type_id ? (docTypes || []).find((type) => type.doc_type_id === tempEditableData.doc_type_id) : null}
                          getOptionLabel={getDocTypeOptionLabel}
                          renderOption={(props, option) => (
                            <li {...props} key={option.doc_type_id}>
                              <span>{option.doc_type_id}</span>
                              <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                            </li>
                          )}
                          filterOptions={filterDocTypeOptions}
                          onChange={(event, newValue) => {
                            if (isReadOnly) return;
                            const nextDocTypeId = newValue ? newValue.doc_type_id : '';
                            const currentDocStatus = tempEditableData.doc_status_id;
                            const nextDocTypeKey = (nextDocTypeId || '').toLowerCase();
                            const nextShouldAutoInclude = AUTO_INCLUDE_IN_NET_TOTALS_DOC_TYPES.has(nextDocTypeKey);
                            const nextDefaultValidDocument = nextShouldAutoInclude
                              ? !EXCLUDED_AUTO_INCLUDE_STATUSES.has((currentDocStatus || '').toLowerCase())
                              : DEFAULT_VALID_DOC_TYPES.has(nextDocTypeKey);

                            changeField('doc_type_id', nextDocTypeId);
                            validateField('doc_type_id', nextDocTypeId);

                            if (nextShouldAutoInclude || !validDocumentTouchedRef.current) {
                              changeField('include_in_net_totals', nextDefaultValidDocument);
                            }

                            if (currentDocStatus) {
                              const allowedStatusesForNextType = computeStatusOptions(nextDocTypeId);
                              const docStatusAllowed = allowedStatusesForNextType.some(
                                (status) => status.doc_status_id === currentDocStatus
                              );

                              if (!docStatusAllowed) {
                                changeField('doc_status_id', '');
                                setFieldError('doc_status_id', REQUIRED_FIELD_ERROR);
                              }
                            } else {
                              setFieldError('doc_status_id', REQUIRED_FIELD_ERROR);
                            }
                            setIsSaved(false);
                          }}
                          onBlur={(e) => onFieldBlur('doc_type_id', e.target.value)}
                          onFocus={() => handleFocusEvent('doc_type_id')}
                          isOptionEqualToValue={(option, value) => option.doc_type_id === value?.doc_type_id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              onKeyDown={handleKeyDown}
                              error={!!fieldErrors.doc_type_id}
                              helperText={fieldErrors.doc_type_id}
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      ) : field === 'doc_status_id' ? (
                        <Autocomplete
                          disabled={isReadOnly}
                          options={statusOptions}
                          value={tempEditableData.doc_status_id ? (docStatuses || []).find((status) => status.doc_status_id === tempEditableData.doc_status_id) : null}
                          getOptionLabel={getDocStatusOptionLabel || ((option) => (!option ? '' : typeof option === 'string' ? option : `${option.doc_status_id} - ${option.name}`))}
                          renderOption={(props, option) => (
                            <li {...props} key={option.doc_status_id}>
                              <span>{option.doc_status_id}</span>
                              <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                            </li>
                          )}
                          filterOptions={filterDocStatusOptions}
                          onChange={(event, newValue, reason) => {
                            if (isReadOnly) return;
                            const nextValue = newValue ? newValue.doc_status_id : '';
                            changeField('doc_status_id', nextValue);
                            if (AUTO_INCLUDE_IN_NET_TOTALS_DOC_TYPES.has((tempEditableData?.doc_type_id || '').toLowerCase()) && nextValue) {
                              changeField('include_in_net_totals', !EXCLUDED_AUTO_INCLUDE_STATUSES.has(nextValue.toLowerCase()));
                            } else if (nextValue === 'rejected') {
                              clearRejectedFinancialFlags();
                            }
                            if (nextValue !== 'rejected' && tempEditableData?.doc_reject_reason_id) {
                              changeField('doc_reject_reason_id', null);
                              applyEmailUploaderFlag(false);
                            }

                            if (reason === 'clear' || nextValue === '') {
                              validateField('doc_status_id', '');
                            } else {
                              validateField('doc_status_id', nextValue);
                            }

                            setIsSaved(false);
                          }}
                          onInputChange={(event, value, reason) => {
                            if (isReadOnly) return;
                            if (reason === 'clear' || (reason === 'input' && value === '')) {
                              changeField('doc_status_id', '');
                              validateField('doc_status_id', '');
                              setIsSaved(false);
                            }
                          }}
                          onBlur={(e) => onFieldBlur('doc_status_id', e.target.value)}
                          onFocus={() => handleFocusEvent('doc_status_id')}
                          isOptionEqualToValue={(option, value) => option.doc_status_id === value?.doc_status_id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required={isRequiredField('doc_status_id')}
                              onKeyDown={handleKeyDown}
                              error={!!fieldErrors.doc_status_id}
                              helperText={fieldErrors.doc_status_id}
                              sx={tempEditableData?.doc_status_id === 'approved'
                                ? {
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': { borderColor: '#2e7d32', borderWidth: 2 },
                                      '&:hover fieldset': { borderColor: '#2e7d32' },
                                      '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                    },
                                  }
                                : undefined}
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      ) : field === 'nominal_code_id' ? (
                        <Autocomplete
                          disabled={isReadOnly}
                          options={uniqueNominalCodes}
                          value={tempEditableData.nominal_code_id ? uniqueNominalCodes.find((nominalCode) => nominalCode.nominal_code_id === tempEditableData.nominal_code_id) : null}
                          getOptionLabel={getNominalCodeOptionLabel}
                          renderOption={(props, option) => (
                            <li {...props} key={option.nominal_code_id}>
                              <span>{option.nominal_code_id}</span>
                              <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                            </li>
                          )}
                          filterOptions={filterNominalCodeOptions}
                          onChange={(event, newValue) => {
                            if (isReadOnly) return;
                            changeField('nominal_code_id', newValue ? newValue.nominal_code_id : null); // Changed to null if no value
                            validateField('nominal_code_id', newValue ? newValue.nominal_code_id : null); // Changed to null if no value
                          }}
                          onBlur={(e) => onFieldBlur('nominal_code_id', e.target.value)}
                          onFocus={() => handleFocusEvent('nominal_code')}
                          isOptionEqualToValue={(option, value) => option.nominal_code_id === value?.nominal_code_id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required={isRequiredField('nominal_code_id')}
                              onKeyDown={handleKeyDown}
                              error={!!fieldErrors.nominal_code_id}
                              helperText={fieldErrors.nominal_code_id}
                              disabled={isReadOnly}
                            />
                          )}
                        />
                      ) : DATE_FIELDS.has(field) ? (
                        <Box sx={{ position: 'relative' }}>
                          <TextField
                            value={field === 'invoice_date' ? invoiceDateDisplay : postingDateDisplay}
                            onChange={handleDateChange(field)}
                            fullWidth
                            required={isRequiredField(field)}
                            error={!!fieldErrors[field]}
                            helperText={fieldErrors[field]}
                            onBlur={() => onFieldBlur(field, field === 'invoice_date' ? invoiceDateDisplay : postingDateDisplay)}
                            onFocus={() => focusPolygon(field === 'invoice_date' ? 'date_of_invoice' : field)}
                            InputProps={{
                              readOnly: isReadOnly,
                              endAdornment: !isReadOnly ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={`open ${field === 'invoice_date' ? 'document' : 'posting'} date picker`}
                                    tabIndex={-1}
                                    onClick={() => openDatePicker(field)}
                                  >
                                    <CalendarMonthIcon />
                                  </IconButton>
                                </InputAdornment>
                              ) : undefined,
                            }}
                            disabled={isReadOnly}
                          />
                          {!isReadOnly && (
                            <input
                              type="date"
                              ref={field === 'invoice_date' ? invoiceDatePickerRef : postingDatePickerRef}
                              value={field === 'invoice_date' ? invoiceIsoValue : postingIsoValue}
                              onChange={handleDatePickerChange(field)}
                              style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                pointerEvents: 'none',
                              }}
                            />
                          )}
                        </Box>
                      ) : (
                        <TextField
                          value={tempEditableData[field] || ''}
                          onChange={(e) => {
                            if (isReadOnly) return;
                            changeField(field, e.target.value);
                          }}
                          onBlur={(e) => onFieldBlur(field, e.target.value)}
                          onFocus={() => focusPolygon(field)}
                          fullWidth
                          required={isRequiredField(field)}
                          error={!!fieldErrors[field]}
                          helperText={fieldErrors[field]}
                          InputProps={{
                            readOnly: isReadOnly,
                            endAdornment: !isReadOnly ? (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="clear field"
                                  tabIndex={-1}
                                  onClick={() => {
                                    changeField(field, '');
                                    validateField(field, '');
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          }}
                          disabled={isReadOnly}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography align="left" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('net')}
                    </Typography>
                  </Box>
                  <TextField
                    value={tempEditableData.net || ''}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      changeField('net', e.target.value);
                    }}
                    fullWidth
                    required={isRequiredField('net')}
                    InputProps={{
                      startAdornment: <Typography variant="subtitle2" sx={{ mr: 1 }}>£</Typography>,
                      endAdornment: !isReadOnly ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear field"
                            tabIndex={-1}
                            onClick={() => {
                              changeField('net', '');
                              onFieldBlur('net', '');
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                      readOnly: isReadOnly,
                    }}
                    error={!!fieldErrors.net}
                    helperText={fieldErrors.net}
                    onBlur={(e) => onFieldBlur('net', e.target.value)}
                    onFocus={() => focusPolygon('net')}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography align="left" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('vat')}
                    </Typography>
                  </Box>
                  <TextField
                    value={tempEditableData.vat || ''}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      changeField('vat', e.target.value);
                    }}
                    fullWidth
                    required={isRequiredField('vat')}
                    InputProps={{
                      startAdornment: <Typography variant="subtitle2" sx={{ mr: 1 }}>£</Typography>,
                      endAdornment: !isReadOnly ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear field"
                            tabIndex={-1}
                            onClick={() => {
                              changeField('vat', '');
                              onFieldBlur('vat', '');
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                      readOnly: isReadOnly,
                    }}
                    error={!!fieldErrors.vat}
                    helperText={fieldErrors.vat}
                    onBlur={(e) => onFieldBlur('vat', e.target.value)}
                    onFocus={() => focusPolygon('vat')}
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1, mb: isReadOnly ? 0 : 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography align="left" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('total')}
                    </Typography>
                  </Box>
                  <TextField
                    value={tempEditableData.total != null && tempEditableData.total !== ''
                      ? `£${tempEditableData.credit_note ? Math.abs(parseFloat(tempEditableData.total)).toFixed(2) : parseFloat(tempEditableData.total).toFixed(2)}`
                      : ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    style={tempEditableData.credit_note ? { backgroundColor: '#ffebeb', color: 'red' } : { backgroundColor: '#f0f0f0' }}
                    error={!!fieldErrors.total}
                    helperText={fieldErrors.total}
                    onFocus={() => focusPolygon('total')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography align="left" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('tax_code_id')}
                    </Typography>
                  </Box>
                  <Autocomplete
                    disabled={isReadOnly}
                    options={taxCodeOptions}
                    value={taxCodeOptions.find((option) => option.tax_code_id === tempEditableData.tax_code_id) || null}
                    getOptionLabel={(option) => (!option ? '' : `${option.tax_code_id} - ${option.name || option.tax_code_id}`)}
                    renderOption={(props, option) => (
                      <li {...props} key={option.tax_code_id}>
                        <span>{option.tax_code_id}</span>
                        <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                      </li>
                    )}
                    filterOptions={filterTaxCodeOptions}
                    onChange={(event, newValue) => {
                      if (isReadOnly) return;
                      const nextValue = newValue ? newValue.tax_code_id : null;
                      changeField('tax_code_id', nextValue);
                      validateField('tax_code_id', nextValue);
                      setIsSaved(false);
                    }}
                    onBlur={(e) => onFieldBlur('tax_code_id', e.target.value)}
                    onFocus={() => handleFocusEvent('tax_code')}
                    isOptionEqualToValue={(option, value) => option.tax_code_id === value?.tax_code_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        onKeyDown={handleKeyDown}
                        error={!!fieldErrors.tax_code_id}
                        helperText={fieldErrors.tax_code_id}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: -0.25, mb: 0 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 0, minHeight: 28 }}>
                    <StyledCheckbox
                      checked={includeInNetTotalsChecked}
                      onChange={(event) => {
                        if (isReadOnly || isRejectedStatus || isIncludeInNetTotalsLocked) return;
                        validDocumentTouchedRef.current = true;
                        changeField('include_in_net_totals', event.target.checked);
                        setIsSaved(false);
                      }}
                      disabled={isReadOnly || isRejectedStatus || isIncludeInNetTotalsLocked}
                      sx={{ m: 0, p: 0, mr: 0.75 }}
                    />
                    <Typography>{getFieldLabel('include_in_net_totals')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 0, minHeight: 28 }}>
                    <StyledCheckbox
                      checked={Boolean(getEffectiveFieldValue('credit_requested'))}
                      onChange={(event) => {
                        if (isReadOnly || isRejectedStatus) return;
                        const checked = event.target.checked;
                        changeField('credit_requested', checked);
                        if (checked) {
                          changeField('credit_amount_requested', requestedCreditAmount.toFixed(2));
                        }
                        setIsSaved(false);
                      }}
                      disabled={isReadOnly || isRejectedStatus}
                      sx={{ m: 0, p: 0, mr: 0.75 }}
                    />
                    <Typography>{getFieldLabel('credit_requested')}</Typography>
                  </Box>
                </Grid>
                {['credit_amount_requested', 'credit_amount_received'].map((field) => (
                  <Grid item xs={12} sm={6} key={field}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography align="left" sx={{ fontWeight: 'bold' }}>
                        {getFieldLabel(field)}
                      </Typography>
                    </Box>
                    <TextField
                      value={getEffectiveFieldValue(field) ?? ''}
                      onChange={(e) => {
                        if (isReadOnly) return;
                        const nextValue = e.target.value;
                        changeField(field, nextValue);
                        if (Number(nextValue) > 0) {
                          changeField('credit_requested', true);
                        }
                        setIsSaved(false);
                      }}
                      fullWidth
                      disabled={isReadOnly || isRejectedStatus || !Boolean(getEffectiveFieldValue('credit_requested'))}
                      InputProps={{
                        startAdornment: <Typography variant="subtitle2" sx={{ mr: 1 }}>£</Typography>,
                        readOnly: isReadOnly,
                        endAdornment: !isReadOnly ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear field"
                              tabIndex={-1}
                              onClick={() => {
                                changeField(field, '0.00');
                                validateField(field, '0.00');
                              }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                      error={!!fieldErrors[field]}
                      helperText={fieldErrors[field]}
                      onBlur={(e) => {
                        const rawValue = e.target.value;
                        const numericValue = Number(rawValue);
                        const formattedValue = rawValue === ''
                          ? '0.00'
                          : Number.isFinite(numericValue)
                            ? numericValue.toFixed(2)
                            : rawValue;
                        changeField(field, formattedValue);
                        validateField(field, formattedValue);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
                {!isReadOnly && (
                  <>
                    <Grid container spacing={2} sx={{ mt: 0.25 }}>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            alignItems: 'stretch',
                            justifyContent: 'stretch',
                            gap: { xs: 1, sm: 1.5 },
                            width: '100%',
                          }}
                        >
                          <Tooltip title="Save changes made to this document" placement="top">
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                disabled={!hasUnsavedChanges || loading || actionLoading}
                                onClick={handleSave}
                                startIcon={<SaveIcon />}
                                sx={{
                                  width: '100%',
                                  px: 2.75,
                                  py: 1.25,
                                }}
                              >
                                Save
                              </Button>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Approve this document" placement="top">
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
                              <Button
                                variant="contained"
                                color="success"
                                disabled={approveDisabled || hasValidationErrors}
                                onClick={handleApproveClick}
                                startIcon={<CheckCircleIcon />}
                                sx={{
                                  width: '100%',
                                  px: 2.75,
                                  py: 1.25,
                                }}
                              >
                                Approve
                              </Button>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Reject this document" placement="top">
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
                              <Button
                                variant="contained"
                                color="error"
                                disabled={rejectDisabled}
                                onClick={handleRejectClick}
                                startIcon={<CancelIcon />}
                                sx={{
                                  width: '100%',
                                  px: 2.75,
                                  py: 1.25,
                                }}
                              >
                                Reject
                              </Button>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Grid>
                    </Grid>

                    <Menu
                      anchorEl={rejectMenuAnchor}
                      open={isRejectMenuOpen}
                      onClose={() => setRejectMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      {rejectableReasons.map((reason) => (
                        <MenuItem
                          key={reason.syntheticKey || reason.doc_reject_reason_id}
                          onClick={() => handleRejectOptionSelect(reason)}
                        >
                          {reason.name || reason.doc_reject_reason_id}
                        </MenuItem>
                      ))}
                      {rejectableReasons.length === 0 && (
                        <MenuItem disabled>No reject reasons available</MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </>
            </Box>
          </CardContent>
        }
        {
          supplierMapping && !isReadOnly && (
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
              <Box position="relative" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%' }}>
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.65)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 4,
                      borderRadius: 1,
                    }}
                  >
                    <CircularProgress size={32} thickness={4} />
                  </Box>
                )}
                <>
                {
                  ['supplier_mapping_id', 'supplier_name', 'supplier_address', 'supplier_telephone', 'supplier_email', 'supplier_website'].map((field) => {
                    const isIdField = field === 'supplier_mapping_id';
                    return (
                    <div key={field} style={{ marginBottom: '10px', marginTop: '10px' }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', textAlign: 'left' }}>
                        {field === 'supplier_mapping_id'? 'Supplier Id': capitalizeField(field)}
                      </Typography>
                      {
                        isIdField ? (
                          <Autocomplete
                            options={suppliers}
                            value={supplierMapping.supplier_mapping_id ? suppliers.find((supplier) => supplier.supplier_id === supplierMapping.supplier_mapping_id) : null}
                            getOptionLabel={getSupplierOptionLabel}
                            renderOption={(props, option) => (
                              <li {...props} key={option.supplier_id}>
                                <span>{option.supplier_id}</span>
                                <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                              </li>
                            )}
                            filterOptions={(options, state) => filterOptions(options, state, true)}
                            onChange={(event, newValue) => {
                              handleSupplierMappingFieldChange('supplier_mapping_id', newValue ? newValue.supplier_id : '');
                              validateField('supplier_mapping_id', newValue ? newValue.supplier_id : '');
                            }}
                            onFocus={() => handleFocusEvent(null)}
                            onBlur={(e) => onFieldBlur('supplier_mapping_id', e.target.value)}
                            isOptionEqualToValue={(option, value) => option.supplier_id === value?.supplier_id}
                            renderInput={(params) => (
                              <TextField {...params} fullWidth onKeyDown={handleKeyDown} error={!!fieldErrors.supplier_mapping_id} helperText={fieldErrors.supplier_mapping_id} />
                            )}
                          />
                        ) : (
                        <TextField
                          value={supplierMapping[field] || ''}
                          onChange={(e) => handleSupplierMappingFieldChange(field, e.target.value)}
                          onBlur={(e) => onFieldBlur(field, e.target.value)}
                          disabled
                          fullWidth
                          error={!!fieldErrors[field]}
                          helperText={fieldErrors[field]}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" >
                                <IconButton
                                  disabled
                                  aria-label="clear field"
                                  tabIndex={-1}
                                  onClick={() => {
                                    handleSupplierMappingFieldChange(field, '');
                                    validateField(field, '');
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </div>
                  );
                  })
                }
                <Box display="flex" flexDirection="column" alignItems="inherit" mt={2} gap={2}>
                  <Button  style={{ "margin-right": '10px' }} variant="outlined" onClick={closeSupplierMappingCard}>Cancel</Button>
                  <Button variant="contained" color={'success'} disabled={ !supplierMapping['supplier_mapping_id'] || !supplierMapping['supplier_name'] } onClick={createSupplierMapping}>Create</Button>
                </Box>
                </>
              </Box>
            </CardContent>
          )
        }
      </Card>

      {/* Status Snackbar */}
      <Snackbar
        anchorOrigin={snackbarAnchor}
        sx={snackbarSx}
        open={!!statusMessage.message}
        autoHideDuration={6000}
        onClose={() => setStatusMessage({ type: '', message: '' })}
      >
        <Alert onClose={() => setStatusMessage({ type: '', message: '' })} severity={statusMessage.type} sx={{ minWidth: 240 }}>
          {statusMessage.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExtractedFields;
